import type { FormEventHandler } from 'react';
import { forwardRef, type MouseEventHandler, useCallback } from 'react';
import { logger } from '../../utils/logger';
import { ButtonVariants } from '../Button';
import { Box, Flex } from '../Core';
import { Modal } from '../Modal/Modal';
import { PopperHeader } from '../PopperHeader/PopperHeader';
import { DialogFooter } from './DialogFooter';
import { DialogContent } from './styles';
import type { DialogProps } from './types';

export const Dialog = forwardRef<HTMLDivElement | null, DialogProps>(
  (
    {
      onConfirm,
      onCancel,
      title,
      headerPrefix,
      headerSuffix,
      headerIcon,
      headerIconColor,
      dataTestId,
      children,
      cancelLabel,
      confirmLabel,
      customActions,
      showCancel = true,
      showConfirm = true,
      close,
      variant = ButtonVariants.Primary,
      confirmDisabled = false,
      confirmLoading = false,
      showClose,
      width,
      contentPadding,
      alignContent,
      contentOverflow = 'auto',
      onConfirmMouseOver,
      preventClosing = false,
      closeOnConfirm = true,
      closeOnCancel = true,
      stretchButtons = false,
      showFooterBorder = true,
      preview,
      suppressFooter = false,
      controlledContent = false,
      onCloseComplete,
      suppressAutoEnterForm,
      confirmButtonType,
      confirmButtonTooltip,
      ...modalProps
    },
    ref
  ) => {
    const handleCancel: MouseEventHandler<HTMLButtonElement> = useCallback(
      e => {
        onCancel && onCancel(e);
        if (closeOnCancel) {
          close();
        }
      },
      [close, onCancel, closeOnCancel]
    );

    const handleConfirm: MouseEventHandler<HTMLElement> = useCallback(
      async event => {
        if (confirmDisabled) {
          return;
        }
        try {
          onConfirm && (await onConfirm(event));
          if (closeOnConfirm) {
            close();
          }
        } catch (error) {
          logger.warn('Error confirming Dialog: ' + (error instanceof Error ? error.message : error));
        }
      },
      [close, onConfirm, closeOnConfirm, confirmDisabled]
    );

    const handleFormSubmit: FormEventHandler<HTMLElement> = useCallback(
      async e => {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm(e as unknown as React.MouseEvent<HTMLElement>);
      },
      [handleConfirm]
    );

    const hasPreview = !!preview;
    const previewStyles: React.CSSProperties = hasPreview
      ? {
          overflow: 'hidden',
          position: 'relative',
        }
      : {};

    return (
      <Modal
        data-testid={dataTestId ?? 'dialog'}
        close={close}
        width={width}
        preventClosing={preventClosing}
        onCloseComplete={onCloseComplete}
        {...modalProps}
        style={{ maxHeight: modalProps.style?.maxHeight ?? '90vh', ...previewStyles }}
        ref={ref}
      >
        <Flex
          as={suppressAutoEnterForm ? undefined : 'form'}
          role={suppressAutoEnterForm ? undefined : 'form'}
          onSubmit={suppressAutoEnterForm ? undefined : handleFormSubmit}
          flexDirection="column"
          overflow="auto"
          flex={1}
        >
          {controlledContent ? (
            // TODO: If you fully control the content,
            // - make sure to set the DialogContent order to 2
            // - make sure to set the DialogFooter order to 3
            // - review examples (like AcceptedCollateralModal) for how to use this
            children
          ) : (
            <>
              <DialogContent
                style={{ order: 2 }}
                align={alignContent}
                overflow={contentOverflow}
                padding={contentPadding}
              >
                {children}
              </DialogContent>

              {!suppressFooter && (
                <Box style={{ order: 3 }}>
                  {customActions
                    ? customActions
                    : (showCancel || showConfirm) && (
                        <DialogFooter
                          cancelLabel={cancelLabel}
                          confirmDisabled={confirmDisabled}
                          confirmLabel={confirmLabel}
                          confirmLoading={confirmLoading}
                          handleCancel={handleCancel}
                          // handleConfirm is not used if the dialog is submitting a form
                          handleConfirm={handleConfirm}
                          onConfirmMouseOver={onConfirmMouseOver}
                          confirmButtonTooltip={confirmButtonTooltip}
                          preventClosing={preventClosing}
                          showCancel={showCancel}
                          showConfirm={showConfirm}
                          showFooterBorder={showFooterBorder}
                          stretchButtons={stretchButtons}
                          variant={variant}
                          confirmButtonType={confirmButtonType}
                        />
                      )}
                </Box>
              )}
            </>
          )}

          {/* We choose to render the header at the bottom of the file, but then order it back to the top in css, in order to avoid
          the close button being auto-focused by the modal (because otherwise it'd be the first focusable element) */}
          {(title || showClose) && (
            <PopperHeader
              boxProps={{ style: { order: 1 } }}
              title={title}
              prefix={headerPrefix}
              suffix={headerSuffix}
              onCloseClicked={showClose ? close : undefined}
              closeDisabled={preventClosing}
              icon={headerIcon}
              iconColor={headerIconColor}
            />
          )}

          {preview}
        </Flex>
      </Modal>
    );
  }
);
